import { useState } from "react";
import { InvoicePositionAdjustment } from "../../types/invoice";
import { ymdToDate } from "../../utils/date";
import { colors, spacing, borderRadius, typography, styles, createButtonStyle } from "../../styles/theme";

type Props = {
  onAdd: (pos: InvoicePositionAdjustment) => void;
  onCancel: () => void;
};

export default function AdjustmentForm({ onAdd, onCancel }: Props) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vat, setVat] = useState<number>(23);
  const [gtuCode, setGtuCode] = useState<string>("GTU_12");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("Wyrównanie");
  const [base, setBase] = useState<number>(0);

  // Walidacja
  const validDates = dateFrom && dateTo && ymdToDate(dateFrom) <= ymdToDate(dateTo);
  const disabled = !(validDates && base > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    onAdd({
      type: "adjustment",
      name,
      date_from: dateFrom,
      date_to: dateTo,
      podstawa_do_wyrownania: base,
      vat,
      description,
      gtu_code: gtuCode || null,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{
        marginBottom: spacing.md,
        padding: spacing.md,
        background: colors.warningLight,
        borderLeft: `3px solid ${colors.warning}`,
        borderRadius: borderRadius.sm,
      }}>
        <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Wyrównanie</strong>
        <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
          Ręczna korekta kwoty (podaj podstawę netto)
        </div>
      </div>

      {/* Nazwa + Data od/do */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Nazwa pozycji:
          </span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Wyrównanie"
            style={styles.input}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Data od:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={styles.input}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Data do:
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={styles.input}
          />
        </label>
      </div>

      {/* Podstawa netto + VAT + Opis */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Podstawa netto (PLN):
          </span>
          <input
            type="number"
            step="0.01"
            value={base}
            onChange={(e) => setBase(Number(e.target.value))}
            style={styles.input}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            VAT (%):
          </span>
          <select value={vat} onChange={(e) => setVat(Number(e.target.value))} style={styles.select}>
            <option value={23}>23%</option>
            <option value={8}>8%</option>
            <option value={0}>0%</option>
            <option value={5}>5%</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            GTU:
          </span>
          <select value={gtuCode} onChange={(e) => setGtuCode(e.target.value)} style={styles.select}>
            <option value="GTU_12">GTU_12</option>
            <option value="">Brak</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Opis (opcjonalny):
          </span>
          <input
            placeholder="Tylko wewnętrznie"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.input}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: spacing.md }}>
        <button type="submit" disabled={disabled} style={createButtonStyle('primary', disabled)}>
          Dodaj pozycję
        </button>
        <button type="button" onClick={onCancel} style={createButtonStyle('secondary')}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
